import UserProfileImage from '@/shared-components/cards/components/UserProfileImage';
import { useDropdownPopover } from '@/shared-components/dropdown';
import ThreeDotsHorizontal from '@/shared-components/icons/components/public/styles/ThreeDotsHorizontal';
import Loader from '@/shared-components/loader/Loader';
import Text from '@/shared-components/text/components/Text';
import { resolveUserProfileImage } from '@/utils/userProfileImage';

import { hasBusinessBilling } from '../utils/billingMode';
import {
  formatRangeDateLabel,
  resolveChannelAvatarProfileType,
} from '../utils/channelPresentation';

export default function ChannelSubscriptionCard({
  subscription,
  isMenuOpen,
  onToggleMenu,
  onActivate,
  onCancel,
  onSwitchBusinessBilling,
  activating,
}) {
  const menuKey = subscription?.channelId ?? null;
  const handleOpenChange = (nextOpen) => {
    if (nextOpen !== isMenuOpen) onToggleMenu?.(menuKey);
  };
  const { rootRef, triggerRef, menuId, toggle, handleTriggerKeyDown } = useDropdownPopover({
    open: isMenuOpen,
    onOpenChange: handleOpenChange,
    disabled: !menuKey,
    idPrefix: `channel-subscription-${menuKey ?? 'actions'}`,
  });
  const ownerUsername = subscription?.ownerUsername
    ? `@${String(subscription.ownerUsername).replace(/^@/, '')}`
    : null;
  const statusLabel = subscription?.statusLabel ?? '';
  const isCancelled = Boolean(subscription?.isCancelled) || statusLabel.toLowerCase() === 'cancelled';
  const joinedLabel = formatRangeDateLabel(subscription?.joinedAt ?? subscription?.periodStartDate ?? null);
  const metaLabel = joinedLabel ? `Joined - ${joinedLabel}` : 'Joined';
  const canActivate = isCancelled && Boolean(subscription?.channelId);
  const canCancel = !isCancelled && Boolean(subscription?.channelId);
  const isBusinessBilling = hasBusinessBilling(subscription);
  const actionLabel = isCancelled ? 'Activate subscription' : 'Cancel subscription';
  const actionDisabled = isCancelled ? (!canActivate || activating) : !canCancel;
  const avatarProfileType = resolveChannelAvatarProfileType({
    ownerType: subscription?.ownerType ?? null,
    channelKind: subscription?.channelKind ?? null,
  });
  const ownerImage = resolveUserProfileImage(
    subscription?.ownerImage,
    subscription?.owner?.user_image,
    subscription?.owner_image,
  );
  const handleAction = () => {
    if (isCancelled) {
      onActivate?.(subscription);
      return;
    }
    onCancel?.(subscription);
  };
  const handleSwitchBusinessBilling = () => {
    onToggleMenu?.(subscription?.channelId ?? null);
    onSwitchBusinessBilling?.(subscription);
  };

  return (
    <div className="profile-channel-subscriptions__card">
      <div className="profile-channel-subscriptions__summary">
        <div className="profile-channel-subscriptions__avatar">
          <UserProfileImage image={ownerImage} typeOfProfile={avatarProfileType} className="profile-channel-subscriptions__avatar-image" />
        </div>
        <div className="profile-channel-subscriptions__text">
          <Text className="profile-channel-subscriptions__title">
            Channel Subscription{ownerUsername ? <> to <strong>{ownerUsername}</strong></> : ''}
          </Text>
          <Text className="profile-channel-subscriptions__meta">{metaLabel}</Text>
        </div>
        <div className="dropdown-menu dropdown-menu--wide profile-channel-subscriptions__actions" ref={rootRef}>
          <button type="button" className="dropdown-menu__trigger" onClick={toggle} onKeyDown={handleTriggerKeyDown} disabled={!menuKey} aria-label="Subscription options" aria-haspopup="menu" aria-expanded={isMenuOpen} aria-controls={menuId} ref={triggerRef}>
            <ThreeDotsHorizontal className="dropdown-menu__icon" />
          </button>
          <div id={menuId} className={`dropdown-menu__menu ${isMenuOpen ? 'is-open' : ''}`} role="menu">
            <button type="button" className="dropdown-menu__item" onClick={handleAction} disabled={actionDisabled}>
              {isCancelled && activating ? <>Activate subscription<Loader inline thin className="profile-channel-subscriptions__action-loader" /></> : actionLabel}
            </button>
            {!isBusinessBilling && (
              <button type="button" className="dropdown-menu__item" onClick={handleSwitchBusinessBilling} disabled={isCancelled}>Buy as business</button>
            )}
          </div>
          <Text className="profile-channel-subscriptions__amount-value">{subscription?.amountLabel ?? 'Amount unavailable'}</Text>
        </div>
      </div>
    </div>
  );
}
